import { Injectable } from '@angular/core';
import { AgentTemplate, CompanyUser, ProfileService } from '@core/services/profile.service';
import { HOME_AGENT_ICON_SVG_MAP, HOME_LOADER_MIN_MS } from './home.constants';

@Injectable()
export class HomePlatformUsersFacade {
  platformUsers: CompanyUser[] = [];
  filteredPlatformUsers: CompanyUser[] = [];
  platformAgents: AgentTemplate[] = [];
  platformDepartmentFilter = '';
  isLoadingPlatformUsers = false;
  platformSortField: 'name' | 'email' | 'department' | 'agents' | 'is_active' | 'query_count' | null = 'name';
  platformSortDirection: 'asc' | 'desc' = 'asc';

  constructor(private profileService: ProfileService) {}

  loadPlatformUsers(): void {
    const startedAt = Date.now();
    this.isLoadingPlatformUsers = true;

    this.profileService.getCompanyUsers().subscribe({
      next: (res) => {
        this.platformUsers = res.users || [];
        this.applyPlatformFilter();
        this.finishLoading(startedAt, (value) => (this.isLoadingPlatformUsers = value));
      },
      error: () => {
        this.finishLoading(startedAt, (value) => (this.isLoadingPlatformUsers = value));
      },
    });

    this.profileService.getAgentTemplates().subscribe({
      next: (agents) => {
        this.platformAgents = agents || [];
      },
      error: () => {},
    });
  }

  applyPlatformFilter(): void {
    if (!this.platformDepartmentFilter) {
      this.filteredPlatformUsers = [...this.platformUsers];
    } else {
      this.filteredPlatformUsers = this.platformUsers.filter((user) => {
        const department = this.getUserDepartment(user);
        return department.toUpperCase() === this.platformDepartmentFilter;
      });
    }

    this.applyPlatformSorting();
  }

  platformSortBy(field: 'name' | 'email' | 'department' | 'agents' | 'is_active' | 'query_count'): void {
    if (this.platformSortField === field) {
      this.platformSortDirection = this.platformSortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.platformSortField = field;
      this.platformSortDirection = 'asc';
    }

    this.applyPlatformSorting();
  }

  getPlatformSortIcon(field: 'name' | 'email' | 'department' | 'agents' | 'is_active' | 'query_count'): string {
    if (this.platformSortField !== field) {
      return 'opacity-50';
    }

    return this.platformSortDirection === 'asc' ? 'rotate-0' : 'rotate-180';
  }

  get platformDepartments(): string[] {
    const departments = new Set<string>();

    this.platformUsers.forEach((user) => {
      const department = this.getUserDepartment(user);
      if (department && department !== '—') {
        departments.add(department.toUpperCase());
      }
    });

    return Array.from(departments).sort();
  }

  getUserDepartment(user: CompanyUser): string {
    if (user.department_name) {
      return user.department_name;
    }

    if (user.department) {
      return typeof user.department === 'string'
        ? user.department
        : user.department.name || '—';
    }

    return '—';
  }

  getUserInitials(user: CompanyUser): string {
    const first = (user.name || '').charAt(0).toUpperCase();
    const last = (user.last_name || '').charAt(0).toUpperCase();
    return first + last || first || '?';
  }

  getUserAvatarColor(user: CompanyUser): string {
    const colors = ['#F59E0B', '#3B82F6', '#10B981', '#EF4444', '#8B5CF6', '#EC4899'];
    return colors[user.id % colors.length];
  }

  getUserQueryCount(user: CompanyUser): number {
    return user.query_count ?? 0;
  }

  getUserAgentIcons(user: CompanyUser): { path: string; bg: string; disabled: boolean }[] {
    const agents = (user as any).agents || (user as any).assigned_agents;
    if (!agents || !Array.isArray(agents)) {
      return this.platformAgents.map((agent) => this.getAgentIconInfo(agent));
    }

    return agents.map((agent: any) => {
      const template = this.platformAgents.find((item) => item.id === (agent.id || agent));
      return template
        ? this.getAgentIconInfo(template)
        : { path: 'assets/images/support.svg', bg: '#E0E7FF', disabled: true };
    });
  }

  private applyPlatformSorting(): void {
    if (!this.platformSortField) {
      return;
    }

    this.filteredPlatformUsers.sort((a, b) => {
      let valueA: any;
      let valueB: any;

      switch (this.platformSortField) {
        case 'name':
          valueA = (`${a.name || ''} ${a.last_name || ''}`).toLowerCase().trim();
          valueB = (`${b.name || ''} ${b.last_name || ''}`).toLowerCase().trim();
          break;
        case 'email':
          valueA = (a.email || '').toLowerCase();
          valueB = (b.email || '').toLowerCase();
          break;
        case 'department':
          valueA = this.getUserDepartment(a).toLowerCase();
          valueB = this.getUserDepartment(b).toLowerCase();
          break;
        case 'agents':
          valueA = this.getUserAgentIcons(a).length;
          valueB = this.getUserAgentIcons(b).length;
          break;
        case 'is_active':
          valueA = a.is_active !== false ? 1 : 0;
          valueB = b.is_active !== false ? 1 : 0;
          break;
        case 'query_count':
          valueA = this.getUserQueryCount(a);
          valueB = this.getUserQueryCount(b);
          break;
        default:
          return 0;
      }

      if (valueA < valueB) {
        return this.platformSortDirection === 'asc' ? -1 : 1;
      }
      if (valueA > valueB) {
        return this.platformSortDirection === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }

  private isAgentNameEnabled(name: string): boolean {
    const normalized = name.toLowerCase();
    return normalized.includes('direcci') && normalized.includes('ceo');
  }

  private getAgentIconInfo(agent: AgentTemplate): { path: string; bg: string; disabled: boolean } {
    const disabled = !this.isAgentNameEnabled(agent.name);
    try {
      const parsed = JSON.parse(agent.icon);
      const info = HOME_AGENT_ICON_SVG_MAP[parsed?.name] || {
        path: 'assets/images/support.svg',
        bg: '#E0E7FF',
      };
      return { ...info, disabled };
    } catch {
      return { path: 'assets/images/support.svg', bg: '#E0E7FF', disabled };
    }
  }

  private finishLoading(startedAt: number, setLoading: (value: boolean) => void): void {
    const elapsed = Date.now() - startedAt;
    const remaining = HOME_LOADER_MIN_MS - elapsed;
    if (remaining > 0) {
      setTimeout(() => setLoading(false), remaining);
      return;
    }

    setLoading(false);
  }
}
